import { RequireProjectAdmin } from '@front/components/auth/RequireProjectAdmin';
import { useSourceSystem } from '@front/hook/Api/SourceSystem';
import { useNavigateBack } from '@front/hook/useNavigateBack';
import { Button, Textarea, TextField, useToast } from '@plaidev/sour-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useProject } from 'auth-lib-front';
import { Controller, useForm } from 'react-hook-form';

export const Route = createFileRoute('/settings/source-systems/new')({
  component: () => (
    <RequireProjectAdmin>
      <SourceSystemNewPage />
    </RequireProjectAdmin>
  ),
});

type FormValues = {
  displayName: string;
  description: string;
};

/**
 * @feature ソースシステム作成
 * @capabilities 表示名・説明の入力, 新規作成
 * @behavior 表示名（必須）と説明を入力→「作成」でソースシステムを登録し一覧へ戻る。プロジェクト管理者専用
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
function SourceSystemNewPage() {
  const navigate = useNavigate();
  const goBackWithFallback = useNavigateBack();
  const toast = useToast();
  const { project } = useProject();
  const createMutation = useSourceSystem.Create();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: { displayName: '', description: '' },
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(async (values) => {
    const trimmedName = values.displayName.trim();
    const trimmedDescription = values.description.trim();
    try {
      await createMutation.mutateAsync({
        projectId: project.id,
        createProps: {
          displayName: trimmedName,
          description: trimmedDescription === '' ? undefined : trimmedDescription,
          isActive: true,
        },
      });
      toast.success('ソースシステムを作成しました');
      void navigate({ to: '/settings/source-systems' });
    } catch (err) {
      const message = err instanceof Error ? err.message : '作成に失敗しました';
      toast.error(message);
    }
  });

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-8">
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="basic" size="sm" onClick={() => goBackWithFallback({ to: '/settings/source-systems' })}>
          戻る
        </Button>
        <h1 className="text-xl font-semibold text-default">ソースシステムの作成</h1>
      </div>

      <form className="flex flex-col gap-6" onSubmit={(e) => void onSubmit(e)}>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-default">
            表示名<span className="text-negative">*</span>
          </span>
          <Controller
            control={control}
            name="displayName"
            rules={{ validate: (v) => v.trim().length > 0 || '表示名を入力してください' }}
            render={({ field }) => (
              <TextField //
                autoFocus
                variant="contained"
                placeholder="例: A社CRM"
                value={field.value}
                onChange={field.onChange}
                required
              />
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-default">説明（任意）</span>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea className="[&>textarea]:w-full" value={field.value} onChange={field.onChange} rows={4} placeholder="説明を入力" />
            )}
          />
        </div>
        <div className="flex gap-3">
          <Button type="submit" variant="primary" loading={createMutation.isPending} disabled={!isValid}>
            作成
          </Button>
          <Button
            type="button"
            variant="basic"
            disabled={createMutation.isPending}
            onClick={() => goBackWithFallback({ to: '/settings/source-systems' })}
          >
            キャンセル
          </Button>
        </div>
      </form>
    </div>
  );
}
